/**
 * CUSTOMER-R4.6 — our latency vs bank-app latency. Never treats app-switch as paid.
 */
import { emitEvent } from "./events-v2.js";

export type BankLaunchTelemetry = {
  click_ms: number;
  chooser_ms: number | null;
  selected_ms: number | null;
  bridge_ms: number | null;
  dispatch_ms: number | null;
  hidden_ms: number | null;
  visible_ms: number | null;
  provider: string | null;
  public_order_number: string | null;
};

const empty = (): BankLaunchTelemetry => ({
  click_ms: 0,
  chooser_ms: null,
  selected_ms: null,
  bridge_ms: null,
  dispatch_ms: null,
  hidden_ms: null,
  visible_ms: null,
  provider: null,
  public_order_number: null,
});

let current: BankLaunchTelemetry = empty();

function now(): number {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
}

function diffs(): Record<string, number | null> {
  const click = current.click_ms;
  const bridge = current.bridge_ms;
  const dispatch = current.dispatch_ms;
  const hidden = current.hidden_ms;
  return {
    click_to_bridge_ms: bridge != null && click ? Math.round(bridge - click) : null,
    bridge_to_dispatch_ms: dispatch != null && bridge != null ? Math.round(dispatch - bridge) : null,
    dispatch_to_page_hidden_ms: hidden != null && dispatch != null ? Math.round(hidden - dispatch) : null,
  };
}

export function markBankLauncherOpenClicked(publicOrderNumber: string | null): void {
  current = { ...empty(), click_ms: now(), public_order_number: publicOrderNumber };
  emitEvent("bank_launcher_open_clicked", { public_order_number: publicOrderNumber });
}

export function markBankChooserOpened(): void {
  current.chooser_ms = now();
}

export function markBankProviderSelected(provider: string): void {
  current.provider = provider;
  current.selected_ms = now();
  emitEvent("bank_launcher_provider_selected", {
    provider,
    public_order_number: current.public_order_number,
  });
}

export function markBankBridgeRendered(): void {
  current.bridge_ms = now();
  emitEvent("bank_bridge_rendered", {
    provider: current.provider,
    public_order_number: current.public_order_number,
    ...diffs(),
  });
}

export function markBankDeeplinkDispatched(ok: boolean): void {
  current.dispatch_ms = now();
  emitEvent("bank_deeplink_dispatched", {
    provider: current.provider,
    ok,
    public_order_number: current.public_order_number,
    ...diffs(),
  });
}

export function markPageBecameHidden(): void {
  if (current.hidden_ms != null) return;
  current.hidden_ms = now();
  emitEvent("page_became_hidden", {
    provider: current.provider,
    public_order_number: current.public_order_number,
    ...diffs(),
  });
}

export function markPageReturnedVisible(): void {
  current.visible_ms = now();
  emitEvent("page_returned_visible", {
    provider: current.provider,
    public_order_number: current.public_order_number,
    ...diffs(),
  });
}

export function markBankLaunchRetry(): void {
  emitEvent("bank_launch_retry", {
    provider: current.provider,
    public_order_number: current.public_order_number,
  });
}

export function markBankLaunchFallback(kind: string): void {
  emitEvent("bank_launch_fallback", {
    kind,
    provider: current.provider,
    public_order_number: current.public_order_number,
  });
}

export function markBankPaymentConfirmed(): void {
  emitEvent("bank_payment_confirmed", {
    public_order_number: current.public_order_number,
  });
}
